<template>
  <Multiselect
    :id="id"
    ref="multiselect"
    :name="name"
    :class="{ 'is-invalid': state === false, [`multiselect-${name}`]: true }"
    :value="value"
    :model-value="value"
    :options="options"
    :internal-search="false"
    :loading="isLoading"
    track-by="id"
    :placeholder="inputPlaceholder"
    select-label=""
    :selected-label="selectedLabel"
    :disabled="disabled"
    :max-height="maxHeight"
    @input="$emit('input', $event)"
    @update:model-value="$emit('input', $event)"
    @search-change="onSearch"
  >
    <template v-slot:singleLabel="{ option }">
      <slot name="label" :option="option">
        {{ customLabel(option) }}
      </slot>
    </template>
    <template v-slot:option="{ option }">
      <slot name="label" :option="option">
        {{ customLabel(option) }}
      </slot>
    </template>
    <template v-slot:noOptions>
      {{ MULTISELECT_NO_OPTIONS }}
    </template>
  </Multiselect>
</template>

<script>
import * as _ from "lodash-es";
import axios from "axios";
import Multiselect from "vue-multiselect";
import { handlePaste } from "@/form-utils";
import { MULTISELECT_NO_OPTIONS } from "@/constants";

export default {
  components: { Multiselect },
  props: {
    id: String,
    name: String,
    state: [Object, Boolean],
    value: Object,
    label: [String, Function],
    otherOption: Object,
    extraTopOption: {
      type: Object,
      required: false,
    },
    url: String,
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    inputPlaceholder: {
      type: String,
      required: false,
      default: "Type to search",
    },
    selectedLabel: String,
    maxCnt: Number,
    maxHeight: Number,
    loadInitialOptions: Boolean,
    params: Object,
    pasteTransformer: Function,
  },
  emits: ["input"],
  data() {
    const options = [];
    if (!_.isNil(this.value)) {
      options.push(this.value);
    }

    if (this.otherOption) {
      const isOther = this.value && this.value.id === this.otherOption.id;
      if (!isOther) {
        options.push(this.otherOption);
      }
    }

    if (this.extraTopOption) {
      const isExtra = this.value && this.value.id === this.extraTopOption.id;
      if (!isExtra) {
        options.unshift(this.extraTopOption);
      }
    }

    return {
      isLoading: false,
      options,
    };
  },
  computed: {
    searchComponent() {
      return this.$refs.multiselect.$refs.search;
    },
  },
  created() {
    this.MULTISELECT_NO_OPTIONS = MULTISELECT_NO_OPTIONS;
  },
  mounted() {
    if (this.loadInitialOptions) {
      this.getOptions("");
    }
    if (this.pasteTransformer) {
      this.searchComponent.addEventListener("paste", this.onPaste);
    }
  },
  beforeDestroy() {
    if (this.pasteTransformer) {
      this.searchComponent.removeEventListener("paste", this.onPaste);
    }
  },
  methods: {
    onPaste(event) {
      handlePaste(event, this.searchComponent, this.pasteTransformer);
      this.$refs.multiselect.updateSearch(this.searchComponent.value);
    },
    getOptions(search) {
      this.isLoading = true;
      axios
        .get(this.url, { params: { ...this.params, search, page_size: this.maxCnt } })
        .then(
          (response) => {
            this.isLoading = false;
            const results = response.data.results;
            if (this.otherOption) {
              results.push(this.otherOption);
            }
            if (this.extraTopOption) {
              results.unshift(this.extraTopOption);
            }
            this.options = results;
          },
          () => {
            this.isLoading = false;
          },
        );
    },
    onSearch: _.debounce(function (search) {
      if (search === "") {
        return;
      }
      this.getOptions(search);
    }, 250),
    customLabel(option) {
      if (!option) {
        return "";
      }
      if (this.otherOption && this.otherOption.id === option.id) {
        return option.name;
      }
      if (this.extraTopOption && this.extraTopOption.id === option.id) {
        return option.name;
      }
      if (_.isFunction(this.label)) {
        return this.label(option);
      }
      return option[this.label];
    },
  },
};
</script>
